import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Card from 'chameleon/ui-stack/ui/base/card';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import styles from './styles.css';

const TodoCard = (props) => {
  const {
    card,
    cardItem,
    date,
    message,
    subMessage,
    timer,
    actionLabel,
    onClick,
    isWelcomeVideo,
    onCloseClick,
    className,
    onFadeOut,
    cardIndex,
    focusedElement,
    redirectFocus,
  } = props;
  const [isFadeOut, setIsFadeOut] = useState(false);
  const [forceDestroy, setForceDestroy] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (
      focusedElement &&
      String(focusedElement) === String(cardItem.id) &&
      !hasFocused
    ) {
      redirectFocus(cardIndex, ref);
      setHasFocused(true);
    }
  }, []);

  const onAnimationEnd = (event) => {
    onFadeOut(event.target);
    setForceDestroy(true);
  };

  const textColor = isWelcomeVideo ? 'text-black' : 'text-white';

  if (!card || forceDestroy) {
    return null;
  }

  const accessibility = {
    ariaHidden: true,
  };
  return (
    <Card
      onAnimationEnd={onAnimationEnd}
      className={cn(
        `w-84 h-60 rounded-b-xl shadow-todo-card ${card.body_bg}`,
        className,
        {
          [styles.fadeout]: isFadeOut,
        },
        styles['animate-left'],
      )}
      direction="vertical"
    >
      <div
        data-test-todo-headline
        className={`px-0.8 h-12 flex items-center ${textColor} text-12 rounded-t-xl ${card.header_bg}`}
      >
        <span id={`card-title-${cardIndex}`}>{card.label}</span>
        <ButtonWithIcon
          isNaked
          isIcon
          size="custom"
          iconWidth="12"
          iconHeight="12"
          icon="close"
          tabIndex="0"
          accessibility={{
            'aria-label': 'Dismiss card',
          }}
          className={cn(
            'ml-auto',
            isWelcomeVideo ? '!text-black' : '!text-white',
          )}
          onClick={(event) => {
            setIsFadeOut(true);
            onCloseClick(event, cardItem);
          }}
          title="Dismiss card"
        />
      </div>
      <A11y>
        <div
          aria-labelledby={`card-action-${cardIndex} card-title-${cardIndex} card-label-${cardIndex}`}
          aria-describedby={`card-date-${cardIndex} card-welcome-${cardIndex}`}
          tabIndex={0}
          className="flex flex-col h-full cursor-pointer"
          onClick={onClick}
          role="link"
          ref={ref}
        >
          <div className="px-0.8 pt-0.8 flex">
            <div className="mr-0.8">
              <Icon
                accessibility={accessibility}
                id={card.icon}
                width="48"
                height="48"
              />
            </div>
            <div>
              {!isWelcomeVideo && (
                <div
                  id={`card-date-${cardIndex}`}
                  className="text-12 text-black opacity-75 leading-7"
                >
                  {date}
                </div>
              )}
              <div
                id={`card-label-${cardIndex}`}
                className="w-56 text-base font-medium text-black leading-8 line-clamp-3 break-words"
              >
                {message}
              </div>
              {isWelcomeVideo && (
                <div
                  id={`card-welcome-${cardIndex}`}
                  className="w-56 text-12 font-thin text-black leading-8 line-clamp-3 break-words"
                >
                  {subMessage}
                </div>
              )}
            </div>
          </div>
          <div className="px-0.8 py-0.8 flex opacity-75 mt-auto border-top leading-7 flex">
            {timer && (
              <div className="flex items-center gap-3.5">
                <Icon
                  accessibility={accessibility}
                  className="-mt-px"
                  id="timer"
                  width="16"
                  height="16"
                />
                <div className="mt-1 text-12 leading-relaxed font-medium">
                  {timer} {timer > 1 ? 'mins' : 'min'}
                </div>
              </div>
            )}
            <div className="flex items-center ml-auto gap-3.5">
              <div
                id={`card-action-${cardIndex}`}
                className="mt-px text-12 leading-relaxed font-medium capitalize"
              >
                {actionLabel.toLowerCase()}
              </div>
              <Icon
                accessibility={accessibility}
                id="forward_arrow"
                width="14"
                height="14"
              />
            </div>
          </div>
        </div>
      </A11y>
    </Card>
  );
};

TodoCard.propTypes = {
  className: PropTypes.string,
  card: PropTypes.object.isRequired,
  cardItem: PropTypes.object.isRequired,
  date: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  actionLabel: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
    .isRequired,
  timer: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCloseClick: PropTypes.func.isRequired,
  onFadeOut: PropTypes.func.isRequired,
};

TodoCard.defaultProps = {
  className: '',
  timer: '',
};

export default TodoCard;
